import { faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormComponent, FormComponentProps } from '../../../../components/form';
import css from './field-select.module.css';
import toFixed from 'accounting-js/lib/toFixed.js';
import { useWatch } from 'react-hook-form';
import {
  Option,
  SelectComponent,
} from '../../../../components/form/elements/select';
import { useService } from '../../../../services/services';
import Paginate from '../../../../interfaces/paginate';
import { CropWorkType } from '../../../../enums/crop-work-types';
import { useParams } from 'react-router-dom';
import Field from '../../../../interfaces/field';
import ClassifierValue from '../../../../interfaces/classifierValue';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';

export default function FieldSelect(
  field: () => FormComponent[],
  name: string,
  title: string
): FormComponent {
  return ({ ...rest }) => (
    <FieldSelectComponent field={field} name={name} title={title} {...rest} />
  );
}

export function FieldSelectComponent({
  field,
  name,
  title,
  parentName,
  formValues,
  ...rest
}: FormComponentProps & {
  field: () => FormComponent[];
  name: string;
  title: string;
  formValues?: object;
}) {
  const { t } = useTranslation();
  const { setValue, getValues, trigger } = useFormContext();
  const tp = (key: string) => t('pages.crop.works.'.concat(key));
  const [totalTreatedArea, setTotalTreatedArea] = useState<number>(0);
  const [usedFields, setUsedFields] = useState([]);
  const [allowAdd, setAllowAdd] = useState(true);
  const [state, setState] = useState({
    count: 0,
    fields: {} as { [key: string]: FormComponent[] },
  });

  const addField = useCallback(() => {
    setState((state) => {
      state.fields[state.count.toString()] = field();
      return {
        ...state,
        count: state.count + 1,
        fields: state.fields,
      };
    });
  }, [field]);

  const removeField = (rowIndex: string) => {
    const fields = getValues('fields');
    const fieldId = fields[rowIndex].field?.value;
    delete fields[rowIndex];
    setValue('fields', fields);
    setUsedFields(usedFields.filter((f) => f !== fieldId));

    trigger('fields');

    setState((state) => {
      delete state.fields[rowIndex];
      return { ...state };
    });
  };

  useEffect(() => {
    const fieldCount = formValues[name]?.length || 1;

    setState((state) => {
      if (state.count < fieldCount) {
        state.fields[state.count.toString()] = field();
        return {
          ...state,
          count: state.count + 1,
          fields: state.fields,
        };
      } else {
        return {
          ...state,
        };
      }
    });
  }, [state.count, formValues, name, addField, field]);

  const fields = useWatch({ name: 'fields' });

  useEffect(() => {
    if (fields) {
      const totalTreatedArea = Object.keys(fields).reduce((acc, key) => {
        const area = parseFloat(
          getValues(name.concat('.', key, '.treated_area'))
        );
        return area ? acc + area : acc;
      }, 0);

      setTotalTreatedArea(totalTreatedArea);

      getValues('products')?.forEach((p: any, p_idx: number) => {
        p.warehouses.forEach((wh: any, wh_idx: number) => {
          let seedingRate = getValues(
            'products.'.concat(
              p_idx.toString(),
              '.warehouses.',
              wh_idx.toString(),
              '.seeding_rate_ha'
            )
          );

          setValue(
            'products.'.concat(
              p_idx.toString(),
              '.warehouses.',
              wh_idx.toString(),
              '.quantity'
            ),
            seedingRate ? toFixed(seedingRate * totalTreatedArea, 3) : ''
          );
        });
      });
    }
  }, [fields, getValues, setValue, name]);

  const totalArea = Object.keys(state.fields).reduce((acc, key) => {
    const area = parseFloat(getValues(name.concat('.', key, '.area')));
    return area ? acc + area : acc;
  }, 0);

  return (
    <div className={css['fields']}>
      {title && <h3>{title}</h3>}
      {Object.keys(state.fields).map((key, index) => (
        <fieldset key={name.concat('-', key)} className={css['field']}>
          <div className={css['body']}>
            <div className={css['remove']}>
              {Object.entries(state.fields).filter(Boolean).length > 1 && (
                <button type="button" onClick={() => removeField(key)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            <div className={css['components']}>
              <FieldSelect2
                cropWorkType={formValues?.type_id}
                name={name}
                keyIndex={Number(key)}
                usedFields={usedFields}
                setUsedFields={setUsedFields}
                setAllowAdd={setAllowAdd}
                key={name.concat('-', key, '-', 'field')}
              />
              {state.fields[key].map((Child, index) => {
                return (
                  <Child
                    key={name.concat('-', key, '-', index.toString())}
                    parentName={(parentName
                      ? parentName.concat('.')
                      : ''
                    ).concat(name, '.', key)}
                    {...rest}
                  />
                );
              })}
            </div>
          </div>
        </fieldset>
      ))}
      {!totalArea || isNaN(totalArea) ? null : (
        <div className={`${css['total']} ${css['area']}`}>
          <div className={css['container']}>
            <div className={css['row']}>
              <span className={css['label']}>{tp('total_area')}</span>
              <span className={css['value']}>{totalArea.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
      {!totalArea || isNaN(totalArea) ? null : (
        <div className={`${css['total']} ${css['treated']}`}>
          <div className={css['container']}>
            <div className={css['row']}>
              <span className={css['label']}>{tp('total_treated_area')}</span>
              <span className={css['value']}>
                {totalTreatedArea.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
      { allowAdd &&
        <div className={css['add']}>
          <button type="button" onClick={addField}>
            <FontAwesomeIcon icon={faPlus} /> {tp('add_field')}
          </button>
        </div>
      }
    </div>
  );
}

export function FieldSelect2({
  cropWorkType,
  name,
  keyIndex,
  usedFields,
  setUsedFields,
  setAllowAdd,
}: {
  cropWorkType: CropWorkType;
  name: string;
  keyIndex: number;
  usedFields: Array<string>;
  setUsedFields: Function;
  setAllowAdd: Function;
}) {
  const { setValue, getValues, trigger } = useFormContext();
  const componentName = name.concat('.', keyIndex.toString(), '.field');
  const fieldWatch = useWatch({ name: componentName });
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.crop.works.'.concat(key)),
    [t]
  );
  const [fieldOptions, setFieldOptions] = useState([]);
  const [filtredFieldOptions, setFiltredFieldOptions] = useState([]);
  const [prevFieldValue, setPrevFieldValue] = useState(null);

  const { fieldService, workService } = useService();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();

  useEffect(() => {
    const fields = getValues('fields');
    if (fieldOptions?.length > 0) {
      const filtredOptions = fieldOptions.filter(
        (option) =>
          !usedFields.includes(option.value) ||
          fields[keyIndex].field?.value === option.value
      );
      setFiltredFieldOptions(filtredOptions);
      setAllowAdd(
        !(
          filtredOptions.length === 0 || 
          (filtredOptions.length === 1 && filtredOptions[0].value === fields[keyIndex].field?.value) ||
          (filtredOptions.length === 1 && fields[keyIndex].field === undefined)
        )
      );
    }
  }, [usedFields, fieldOptions, getValues, keyIndex, setAllowAdd]);

  useEffect(() => {
    const fieldId = fieldWatch?.value ?? fieldWatch;
    if (prevFieldValue !== fieldId) {
      setPrevFieldValue(fieldId);
      const params = {
        active: 'yes',
        crop_id: null as string,
        not_crop_group_values: [] as string[],
        without_empty_crop: false,
        work_type: cropWorkType,
        unactiveIds: [fieldId ?? ''],
      };

      if (
        cropWorkType === CropWorkType.FRUIT_HARVESTING ||
        cropWorkType === CropWorkType.VEGETABLE_ETC_HARVESTING
      ) {
        // find crop from previous fields
        for (let i = keyIndex - 1; i >= 0; i--) {
          params.crop_id = getValues(
            'fields.'.concat(i.toString(), '.crop_id')
          );
          if (params['crop_id'] !== null) {
            break;
          }
        }
      }

      params.without_empty_crop =
        workService.workTypeExpectsFieldHasCrop(cropWorkType);

      params.not_crop_group_values =
        workService.forbiddenCropGroupsForWorkType(cropWorkType);

      fieldService
        .index(farmId, season, params, true)
        .subscribe((response: Paginate<Field>) => {
          const opts = response.data.map((field: Field) => {
            return {
              label: field.name,
              value: field.id,
              field: field,
            };
          });
          const selectedFields = getValues('fields').map(
            ({ field }) => field?.value
          ) as string[];

          setFieldOptions(opts);
          setUsedFields(selectedFields);
        });

      const selectedOption = fieldOptions.find(
        (field) => field.value === fieldId
      );
      const field = selectedOption?.field;
      if (field) {
        setValue(
          name.concat('.', keyIndex.toString(), '.field'),
          selectedOption
        );
        setValue(
          name.concat('.', keyIndex.toString(), '.treated_area'),
          field.area
        );
        setValue(
          name.concat('.', keyIndex.toString(), '.lad_number'),
          field.lad_number
        );
        setValue(name.concat('.', keyIndex.toString(), '.area'), field.area);
        setValue(
          name.concat('.', keyIndex.toString(), '.crop_value'),
          field.crop_value
        );
        const work: Option & { work: ClassifierValue } = getValues('work');
        if (field.crop_value && work?.work?.properties) {
          const deleteFieldCrop = Object.values(work?.work?.properties).find(
            (property) =>
              property?.property?.key ===
              ClassifierPropertyKey.DELETE_FIELD_CROP
          )?.value;
          setValue(
            name.concat('.', keyIndex.toString(), '.delete_field_crop'),
            deleteFieldCrop === true
          );
        }
        trigger(name);
      }
    }
  }, [
    cropWorkType,
    farmId,
    fieldOptions,
    fieldService,
    fieldWatch,
    getValues,
    keyIndex,
    name,
    prevFieldValue,
    season,
    setUsedFields,
    setValue,
    trigger,
    workService,
  ]);

  return (
    <SelectComponent
      name={componentName}
      multi={false}
      label={tp('field')}
      cacheOn={[]}
      grouped={false}
      inline={false}
      asyncOptions={null}
      options={filtredFieldOptions}
    />
  );
}
